/**
 * HEIF/HEIC container metadata (§20, §8).
 *
 * HEIC is an HEVC-coded image inside a HEIF (ISO base media) container. This
 * project deliberately does not bundle an HEVC decoder — the only good open one
 * is LGPL-3.0, which the licensing decision in §32 rules out — so the pixels
 * are the platform's job, exactly as playback is for audio and video (§20). A
 * browser or OS that can decode HEIC renders it through an `<img>`; one that
 * cannot is told so plainly rather than shown a blank frame (§19).
 *
 * What this reads is the container: how big the image is and which way up it
 * goes, so the viewer can show the photo's real dimensions and orientation even
 * on a device that cannot decode the pixels. No sample data is touched, and
 * every box size is validated against its parent so a malformed file cannot
 * drive the reader out of bounds (§30).
 */
export interface HeifInfo {
  readonly width: number | null;
  readonly height: number | null;
  /** Clockwise rotation to apply for display: 0, 90, 180 or 270. */
  readonly rotationDeg: number;
  /** The `ftyp` major brand: `heic`, `heix`, `mif1`, `heif`, … */
  readonly brand: string;
  readonly hasThumbnail: boolean;
}

interface Box { readonly type: string; readonly start: number; readonly end: number; readonly headerLen: number }

function fourcc(view: DataView, offset: number): string {
  return String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));
}

function readBoxes(view: DataView, from: number, to: number): Box[] {
  const boxes: Box[] = [];
  let pos = from;
  while (pos + 8 <= to) {
    let size = view.getUint32(pos, false);
    const type = fourcc(view, pos + 4);
    let headerLen = 8;
    if (size === 1) {
      if (pos + 16 > to || view.getUint32(pos + 8, false) !== 0) break;
      size = view.getUint32(pos + 12, false);
      headerLen = 16;
    } else if (size === 0) {
      size = to - pos;
    }
    const end = pos + size;
    if (size < headerLen || end > to) break;
    boxes.push({ type, start: pos, end, headerLen });
    pos = end;
  }
  return boxes;
}

function find(view: DataView, boxes: readonly Box[], type: string): Box | undefined {
  return boxes.find((b) => b.type === type);
}

/** `true` when this looks like a HEIF-family file. */
export function isHeif(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const top = readBoxes(view, 0, bytes.length);
  const ftyp = find(view, top, 'ftyp');
  if (!ftyp) return false;
  const brand = fourcc(view, ftyp.start + ftyp.headerLen);
  return HEIF_BRANDS.has(brand) || compatibleBrand(view, ftyp);
}

const HEIF_BRANDS = new Set(['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'mif1', 'msf1', 'heif']);

function compatibleBrand(view: DataView, ftyp: Box): boolean {
  // Compatible brands follow the major brand + minor version, 4 bytes each.
  for (let p = ftyp.start + ftyp.headerLen + 8; p + 4 <= ftyp.end; p += 4) {
    if (HEIF_BRANDS.has(fourcc(view, p))) return true;
  }
  return false;
}

export function parseHeif(bytes: Uint8Array): HeifInfo | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const top = readBoxes(view, 0, bytes.length);
  const ftyp = find(view, top, 'ftyp');
  const meta = find(view, top, 'meta');
  if (!meta) return null;
  const brand = ftyp ? fourcc(view, ftyp.start + ftyp.headerLen) : '';

  // `meta` is a FullBox: four bytes of version/flags precede its children.
  const metaChildren = readBoxes(view, meta.start + meta.headerLen + 4, meta.end);
  const iprp = find(view, metaChildren, 'iprp');
  const ipco = iprp ? find(view, readBoxes(view, iprp.start + iprp.headerLen, iprp.end), 'ipco') : undefined;

  let width: number | null = null;
  let height: number | null = null;
  let rotationDeg = 0;

  if (ipco) {
    let bestArea = -1;
    for (const prop of readBoxes(view, ipco.start + ipco.headerLen, ipco.end)) {
      if (prop.type === 'ispe') {
        // ispe: version/flags(4), width(4), height(4). The largest is the
        // primary image; smaller ones are thumbnails.
        const w = view.getUint32(prop.start + prop.headerLen + 4, false);
        const h = view.getUint32(prop.start + prop.headerLen + 8, false);
        if (w * h > bestArea) { bestArea = w * h; width = w; height = h; }
      } else if (prop.type === 'irot') {
        // irot: one byte, low two bits = counter-clockwise quarter turns.
        const ccw = view.getUint8(prop.start + prop.headerLen) & 0x03;
        rotationDeg = (360 - ccw * 90) % 360;
      }
    }
  }

  // A `thmb` item reference means a thumbnail is present.
  const iref = find(view, metaChildren, 'iref');
  const hasThumbnail = iref
    ? readBoxes(view, iref.start + iref.headerLen + 4, iref.end).some((b) => b.type === 'thmb')
    : false;

  return { width, height, rotationDeg, brand, hasThumbnail };
}
